import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Archive, Award, BadgeCheck, Ban, BarChart3, Bell, BookHeart, BookOpen, Bot, Brain, Briefcase, Calendar, Camera, Castle, Check, CheckCircle, ChevronLeft, ChevronRight, Circle, CircleDot, Clapperboard, ClipboardList, Coffee, Coins, Crown, Disc, Drama, Eye, EyeOff, FileEdit, FileText, Film, Flame, Flower, FolderOpen, Footprints, Gamepad2, Gem, Gift, Globe, GraduationCap, Handshake, Heart, HeartHandshake, HelpCircle, Home, Image, Infinity, Landmark, Layers, Library, Lightbulb, Link2, Lock, Mail, Map, MapPin, MessageCircle, Mic, Monitor, Moon, Music, Newspaper, Palette, Palmtree, PartyPopper, PawPrint, PenLine, Phone, Podcast, PoundSterling, Radio, Rainbow, RefreshCw, Scale, ScrollText, Search, Shield, ShoppingBag, SlidersHorizontal, Smartphone, Sparkles, Sprout, Star, Tablet, Target, Ticket, TrendingUp, Trophy, Type, User, UserPlus, Users, Video, Wind, Wrench, Zap } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AppPage = () => {
  // Members app subscription link
  const subscriptionJoinLink = 'https://members.popcrownofficial.com/subscription/join';

  // FAQ Schema for rich snippets in Google search
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is the PopCrown app free?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. The PopCrown app is free — install it directly to your iPhone or Android from your browser today (App Store and Google Play listings coming soon). You get daily MJ quizzes & games, the Legal Vault, In His Words, the global leaderboard and 130+ language support, with no card needed and no ads."
        }
      },
      {
        "@type": "Question",
        "name": "What is the PopCrown Community?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The PopCrown Community is the optional paid layer on top of the free PopCrown app. PopCrown Community members get the community feed, early access to event tickets, exclusive discount codes (merch, tickets, partner perks), Fan Points and members-only badges. It costs £1.99/month."
        }
      },
      {
        "@type": "Question",
        "name": "Why does the PopCrown Community have a fee if the app is free?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The app is free because we want every MJ fan to be able to use it. The PopCrown Community fee directly funds hosting, security, moderation and legal compliance — keeping the community safe, ad-free and properly maintained. The community funds the community, not advertisers."
        }
      },
      {
        "@type": "Question",
        "name": "Is PopCrown affiliated with the Michael Jackson Estate?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No, PopCrown is an independent fan community and is not affiliated with any artist estates, rights holders, or official Michael Jackson organisations. We're fans building for fans."
        }
      },
      {
        "@type": "Question",
        "name": "What devices can I use PopCrown on?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "PopCrown is available on iOS (iPhone/iPad), Android phones and tablets, and via web browser on any desktop computer. You can access your membership across all devices with a single account."
        }
      },
      {
        "@type": "Question",
        "name": "How much does the PopCrown Community cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "PopCrown Community membership is £1.99/month. The PopCrown app itself remains completely free with no card required."
        }
      }
    ]
  };

  // Organization Schema
  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "PopCrown Official",
    "url": "https://popcrownofficial.com",
    "logo": "https://customer-assets.emergentagent.com/job_poplegacy/artifacts/p5j56ube_Main%20Logo%20Name%20%282%29.png",
    "description": "The ultimate safe space for Michael Jackson fans worldwide. Join the PopCrown community for exclusive content, moderated discussions, and fan events.",
    "sameAs": []
  };

  // Feature Carousel State
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const carouselRef = useRef(null);

  // Feature categories with FOMO and psychological sales language
  const featureSlides = [
    {
      id: 'podcast',
      icon: <Podcast className="w-10 h-10" />,
      badge: 'COMING SOON',
      badgeColor: '#E10600',
      title: 'PopCrown Founders Show',
      subtitle: 'YOUTUBE & PODCAST',
      color: '#DC143C',
      description: 'Weekly content from the PopCrown Founders - interviews, reviews, discussions and more!',
      highlights: [
        { text: 'Weekly YouTube episodes', icon: <Monitor className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Audio podcast on all platforms', icon: <Mic className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Exclusive members-only broadcasts', icon: <Crown className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Fan interviews from the community', icon: <Mic className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Behind the scenes at PopCrown', icon: <Clapperboard className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Album, song & video deep-dive reviews', icon: <Music className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Michael Biopic 2026 discussions', icon: <Clapperboard className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Some episodes are EXCLUSIVE to Gold Members only. Get the inside scoop first!'
    },
    {
      id: 'profile',
      icon: <User className="w-10 h-10" />,
      title: 'Your Fan Identity',
      subtitle: 'PERSONAL PROFILE',
      color: '#8B5CF6',
      description: 'Stand out in the community with your unique fan identity.',
      highlights: [
        { text: 'Custom photo & display name', icon: <Camera className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Your membership tier BADGE displayed', icon: <Crown className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Points total shown to all', icon: <Star className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Follower & following counts', icon: <Users className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Your posts & memorabilia gallery', icon: <Image className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Privacy controls you own', icon: <Lock className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'The higher your tier, the more respect you command. Start climbing TODAY!'
    },
    {
      id: 'community',
      icon: <Users className="w-10 h-10" />,
      title: 'Your Fan Family',
      subtitle: 'COMMUNITY FEED',
      color: '#9333EA',
      description: 'Finally, a place where being a true fan is celebrated, not judged.',
      highlights: [
        { text: 'Share posts, photos & videos', icon: <Camera className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Upload up to 10 photos per post', icon: <Image className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: '60-second videos (200MB)', icon: <Video className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Embed YouTube videos', icon: <Sparkles className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: '@mentions & #hashtags', icon: '#️⃣' },
        { text: 'See who liked YOUR posts', icon: <Eye className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'While others argue on toxic platforms, YOU\'RE in a safe haven with real fans!'
    },
    {
      id: 'support',
      icon: <Heart className="w-10 h-10" />,
      badge: 'HEALING',
      badgeColor: '#10B981',
      title: 'You\'re Not Alone',
      subtitle: '12 SUPPORT ROOMS',
      color: '#10B981',
      description: 'A safe space for life\'s challenges - because our community supports each other.',
      highlights: [
        { text: 'Mental Health', icon: <Brain className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Bereavement & Grief', icon: <Heart className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Burnout & Overwhelm', icon: <Wind className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Relationships', icon: <Heart className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Career', icon: <Briefcase className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Neurodivergence', icon: <Rainbow className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Loneliness & Belonging', icon: <Users className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Money Stress', icon: <PoundSterling className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Sleep & Fatigue', icon: <Moon className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Healthy Habits', icon: <Sprout className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Menopause Support', icon: <Flower className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'General Support', icon: <Handshake className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Share anonymously — NO judgement', icon: <EyeOff className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Where else can you find people who TRULY understand? This community HEALS.'
    },
    {
      id: 'horoscope',
      icon: <Moon className="w-10 h-10" />,
      badge: 'DAILY',
      badgeColor: '#6366F1',
      title: 'Pop Icon-Themed Guidance',
      subtitle: 'DAILY HOROSCOPE',
      color: '#6366F1',
      description: 'Your daily cosmic reading infused with Michael\'s wisdom.',
      highlights: [
        { text: 'All 12 zodiac signs', icon: <CircleDot className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: '365+ unique readings per sign', icon: <Calendar className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'References classic songs & albums', icon: <Music className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'NEW reading every single day', icon: <Sparkles className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Earn 5 points daily', icon: <Star className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Beautiful animated interface', icon: <Star className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Start EVERY day with Michael\'s spirit guiding you. Don\'t miss a single reading!'
    },
    {
      id: 'cards',
      icon: <Layers className="w-10 h-10" />,
      title: 'Spiritual Guidance',
      subtitle: 'CARD READINGS',
      color: '#EC4899',
      description: 'Pop icon-inspired spiritual messages to guide your journey.',
      highlights: [
        { text: 'Daily single card guidance', icon: <Sparkles className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: '3-Card Spread: Past, Present, Future', icon: <Sparkles className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Pop icon-inspired spiritual messages', icon: <Sparkles className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Beautiful exclusive artwork', icon: <Palette className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Earn points for readings', icon: <Star className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'These cards are ONLY available here. Nowhere else will you find this spiritual guidance!'
    },
    {
      id: 'games',
      icon: <Gamepad2 className="w-10 h-10" />,
      badge: '30+ GAMES & QUIZZES',
      badgeColor: '#DC143C',
      title: 'Endless Entertainment',
      subtitle: 'PLAY — INTERACTIVE GAMES',
      color: '#22C55E',
      description: 'Think you know everything? PROVE IT!',
      highlights: [
        { text: 'PopCrown Wordle (MJordle)', icon: <Type className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Word Search, Scramble & Chain', icon: <FileEdit className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Hangman & Cryptogram', icon: <Target className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Finish the Lyric & Lyric Quiz', icon: <Music className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Video Quiz & Photo Puzzle', icon: <Clapperboard className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Dance Move & Outfit Game', icon: <Sparkles className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Daily Quiz, Trivia & Daily Poll', icon: <HelpCircle className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Archive Hunt, Michael Movie Quiz & more', icon: <Trophy className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: '30+ ways to prove you\'re the ULTIMATE fan. Daily streaks = bragging rights!'
    },
    {
      id: 'leaderboard',
      icon: <Trophy className="w-10 h-10" />,
      title: 'Rise Through the Ranks',
      subtitle: 'POINTS & LEADERBOARD',
      color: '#E10600',
      description: 'Earn your crown! 20 themed tiers from New Moonwalker to THE KING.',
      highlights: [
        { text: 'Earn points through EVERYTHING', icon: <Star className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: '20 legendary tiers', icon: <Award className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'From "New Moonwalker" to "The King"', icon: <Crown className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'GLOBAL leaderboard rankings', icon: <Globe className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Weekly & all-time rankings', icon: <BarChart3 className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Unlock exclusive achievements', icon: <Trophy className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Your rank shows the WORLD how dedicated you are. Will YOU become The King?'
    },
    {
      id: 'news',
      icon: <Newspaper className="w-10 h-10" />,
      title: 'Never Miss a Story',
      subtitle: 'DAILY NEWS',
      color: '#0EA5E9',
      description: 'All the latest news in one place - estate, family, industry, fan community.',
      highlights: [
        { text: 'Latest related news', icon: <Newspaper className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Estate announcements', icon: <Landmark className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Family updates', icon: <Users className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Industry & fan news', icon: <Globe className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Updated regularly', icon: <RefreshCw className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Be the FIRST to know everything. Your friends will ask YOU for updates!'
    },
    {
      id: 'blog',
      icon: <BookOpen className="w-10 h-10" />,
      title: 'Deep Dive Articles',
      subtitle: 'BLOG',
      color: '#8B5CF6',
      description: 'In-depth articles, fan perspectives, and historical deep-dives.',
      highlights: [
        { text: 'In-depth articles', icon: <BookOpen className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Fan perspectives', icon: <MessageCircle className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Historical deep-dives', icon: <Landmark className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Regular new posts', icon: <PenLine className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Knowledge is power. Know MORE than anyone else about Michael!'
    },
    {
      id: 'timeline',
      icon: <Calendar className="w-10 h-10" />,
      title: 'Journey Through History',
      subtitle: 'COMPLETE TIMELINE',
      color: '#6366F1',
      description: 'Every milestone from 1958 onwards - nothing left out.',
      highlights: [
        { text: 'Year-by-year journey', icon: <Calendar className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Career milestones & awards', icon: <Trophy className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Album releases & tours', icon: <Disc className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Personal moments', icon: <HeartHandshake className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Beautiful visual timeline', icon: <Sparkles className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'The most COMPLETE timeline anywhere. Impress everyone with your knowledge!'
    },
    {
      id: 'discography',
      icon: <Music className="w-10 h-10" />,
      title: 'The Complete Collection',
      subtitle: 'MUSIC',
      color: '#EC4899',
      description: 'Every album, every track, every story behind the music.',
      highlights: [
        { text: 'Every album covered', icon: <Disc className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Track listings & artwork', icon: <Palette className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Chart positions', icon: <TrendingUp className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Album stories & trivia', icon: <BookOpen className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Production credits', icon: <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'From Off the Wall to Invincible - become a walking encyclopedia!'
    },
    {
      id: 'videos',
      icon: <Film className="w-10 h-10" />,
      title: 'The Short Films',
      subtitle: 'VIDEOS',
      color: '#F97316',
      description: 'Watch the videos that revolutionized MTV - all in one place.',
      highlights: [
        { text: 'Complete video collection', icon: <Clapperboard className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Watch within the app', icon: <Sparkles className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Behind-the-scenes facts', icon: <Drama className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Video information', icon: <Sparkles className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Thriller, Beat It, Smooth Criminal - relive the magic anytime!'
    },
    {
      id: 'interviews',
      icon: <Mic className="w-10 h-10" />,
      title: 'Hear His Voice',
      subtitle: 'IN HIS OWN WORDS',
      color: '#F97316',
      description: 'Direct from Michael - interviews and insights by topic.',
      highlights: [
        { text: 'Interview clips & segments', icon: <Mic className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Categorized by subject', icon: <FolderOpen className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Historic interviews', icon: <Landmark className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'His philosophy & wisdom', icon: <MessageCircle className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Hear the REAL Michael. His words will move you to tears!'
    },
    {
      id: 'legal',
      icon: <Scale className="w-10 h-10" />,
      title: 'Facts Over Fiction',
      subtitle: 'LEGAL VAULT',
      color: '#0EA5E9',
      description: 'Court documents. FBI files. Trial transcripts. THE TRUTH.',
      highlights: [
        { text: 'Court documents', icon: <ScrollText className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'FBI investigation files', icon: <Search className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Trial transcripts', icon: <Scale className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Factual timelines', icon: <BarChart3 className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Fully sourced evidence', icon: <CheckCircle className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'ARM yourself with FACTS. Shut down haters with documented TRUTH!'
    },
    {
      id: 'vindication',
      icon: <Shield className="w-10 h-10" />,
      title: 'The Truth Documented',
      subtitle: 'VINDICATION',
      color: '#22C55E',
      description: 'Facts, evidence, and resources to clear misconceptions.',
      highlights: [
        { text: 'Documented evidence', icon: <ClipboardList className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Clearing misconceptions', icon: <Check className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Factual resources', icon: <BookOpen className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Primary sources', icon: <Link2 className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Be a WARRIOR for truth. You\'ll have the receipts!'
    },
    {
      id: 'books',
      icon: <Library className="w-10 h-10" />,
      title: 'Read Everything',
      subtitle: 'BOOK LIBRARY',
      color: '#84CC16',
      description: '340+ books curated by Angie - biographies, photo books, studies.',
      highlights: [
        { text: '340+ related books', icon: <BookOpen className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Biographies & autobiographies', icon: <BookOpen className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Photo books & children\'s books', icon: <Image className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Academic studies', icon: <GraduationCap className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Angie\'s curated collection', icon: <Star className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Purchase links included', icon: <ShoppingBag className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: '340 BOOKS! You could read for YEARS and still not finish!'
    },
    {
      id: 'tourism',
      icon: <MapPin className="w-10 h-10" />,
      title: 'Walk Where He Walked',
      subtitle: 'POP ICON TOURISM',
      color: '#EF4444',
      description: 'Plan your pilgrimage to every location that matters.',
      highlights: [
        { text: 'Neverland Ranch', icon: <Castle className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: '2300 Jackson Street, Gary', icon: <Home className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Hayvenhurst & Motown Museum', icon: <Landmark className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Apollo Theater & Forest Lawn', icon: <Drama className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Photos, addresses & directions', icon: <Map className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Share YOUR visits', icon: <Camera className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Imagine standing where Michael stood. Make your pilgrimage UNFORGETTABLE!'
    },
    {
      id: 'worldtour',
      icon: <Map className="w-10 h-10" />,
      title: 'Global Concert Map',
      subtitle: 'WORLD TOUR MAP',
      color: '#3B82F6',
      description: 'Interactive map of every concert location worldwide.',
      highlights: [
        { text: 'Interactive global map', icon: <Map className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Every concert location', icon: <MapPin className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Historical tour dates', icon: <Calendar className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Tour statistics', icon: <BarChart3 className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'See how Michael conquered the ENTIRE WORLD!'
    },
    {
      id: 'podcasts',
      icon: <Podcast className="w-10 h-10" />,
      title: 'Listen & Learn',
      subtitle: 'FAN PROJECTS & PODCASTS',
      color: '#A855F7',
      description: 'Curated podcasts from passionate advocates.',
      highlights: [
        { text: 'Faking Michael', icon: <Mic className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Case for Innocence', icon: <Scale className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Club 30s Fan Girl', icon: <Heart className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Humanitarian Podcast', icon: <Heart className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Direct links to listen', icon: <Link2 className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Hours of content from fans who\'ve done the DEEP research!'
    },
    {
      id: 'annie',
      icon: <Bot className="w-10 h-10" />,
      title: 'Your AI Companion',
      subtitle: 'ANNIE AI',
      color: '#06B6D4',
      description: 'Ask ANYTHING about Michael - Annie knows it all!',
      highlights: [
        { text: 'AI-powered assistant', icon: <Bot className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Free: 3 questions per day', icon: <BadgeCheck className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'PopCrown Community: UNLIMITED questions', icon: <Infinity className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Instant answers 24/7', icon: <Zap className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Personalised recommendations', icon: <Sparkles className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Conversational interface', icon: <MessageCircle className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Like having a GENIUS in your pocket. Members ask as much as they like!'
    },
    {
      id: 'wwmd',
      icon: <BookHeart className="w-10 h-10" />,
      title: 'Live His Legacy',
      subtitle: 'WWMD PROGRAMME',
      color: '#EC4899',
      description: '"What Would Michael Do?" — a 90-day programme. Spread love like he did.',
      highlights: [
        { text: '90-day WWMD programme', icon: <HeartHandshake className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Humanitarian inspiration', icon: <Globe className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'My Journey tracker', icon: <TrendingUp className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Kindness challenges', icon: <Handshake className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Community initiatives', icon: <Users className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Be the CHANGE Michael wanted to see. His spirit lives through YOU!'
    },
    {
      id: 'archives',
      icon: <Archive className="w-10 h-10" />,
      title: 'Historical Records',
      subtitle: 'THE CADMAN ARCHIVES',
      color: '#78716C',
      description: 'Decades of newspaper clippings - primary sources preserved.',
      highlights: [
        { text: 'Original newspaper clippings', icon: <Newspaper className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Decades of coverage', icon: <Calendar className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Searchable archive', icon: <Search className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Primary sources', icon: <ClipboardList className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'See history AS IT HAPPENED. Priceless historical records!'
    },
    {
      id: 'biopic',
      icon: <Clapperboard className="w-10 h-10" />,
      badge: 'HOT',
      badgeColor: '#DC143C',
      title: '2026 Movie Updates',
      subtitle: 'MICHAEL BIOPIC',
      color: '#E10600',
      description: 'Everything about the BIGGEST movie event of 2026!',
      highlights: [
        { text: 'Latest news & updates', icon: <Newspaper className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Jaafar Jackson starring!', icon: <Star className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Behind-the-scenes news', icon: <Clapperboard className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Cast announcements', icon: <Drama className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Release information', icon: <Calendar className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Be the FIRST to know everything. This movie will BREAK the internet!'
    },
    {
      id: 'social',
      icon: <Users className="w-10 h-10" />,
      title: 'Build Your Network',
      subtitle: 'SOCIAL FEATURES',
      color: '#8B5CF6',
      description: 'Follow fans, build connections, grow your community.',
      highlights: [
        { text: 'Follow other members', icon: <UserPlus className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'See followers/following', icon: <Users className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Profile visits', icon: <Eye className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Discover new fans', icon: <Search className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Community connections', icon: <Handshake className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Your TRIBE is here. Make lifelong friends who GET it!'
    },
    {
      id: 'notifications',
      icon: <Bell className="w-10 h-10" />,
      title: 'Never Miss a Beat',
      subtitle: 'NOTIFICATIONS',
      color: '#E10600',
      description: 'Push notifications so you NEVER miss important moments.',
      highlights: [
        { text: 'Push notifications (PWA)', icon: <Bell className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'New follower alerts', icon: <User className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Like & comment notifications', icon: <Heart className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'New episode alerts', icon: <Circle className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Likes & comments', icon: <Heart className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'New content drops? You\'ll know INSTANTLY. Never miss the action!'
    },
    {
      id: 'safety',
      icon: <Lock className="w-10 h-10" />,
      title: 'Peace of Mind',
      subtitle: 'SAFETY & PRIVACY',
      color: '#22C55E',
      description: 'A community built on respect and shared passion for Michael.',
      highlights: [
        { text: 'Block users instantly', icon: <Ban className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Report content/users', icon: <AlertTriangle className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'AI-powered moderation', icon: <Bot className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'No ads - EVER', icon: <Ban className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'No data selling - EVER', icon: <Lock className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'GDPR compliant', icon: <CheckCircle className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'We PROTECT you. This is the SAFEST fan community online!'
    },
    {
      id: 'search',
      icon: <Search className="w-10 h-10" />,
      title: 'Find Everything',
      subtitle: 'SEARCH',
      color: '#F97316',
      description: 'Discover members, posts, and content instantly.',
      highlights: [
        { text: 'Find members by name', icon: <User className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Search all posts', icon: <FileEdit className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Hashtag discovery', icon: '#️⃣' },
        { text: 'Content search', icon: <Search className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Instant results', icon: <Zap className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Never miss a conversation. Find EXACTLY what you\'re looking for!'
    },
    {
      id: 'help',
      icon: <HelpCircle className="w-10 h-10" />,
      title: 'We\'ve Got You',
      subtitle: 'FAQs & HELP',
      color: '#6366F1',
      description: 'Comprehensive support whenever you need it.',
      highlights: [
        { text: 'Comprehensive help center', icon: <BookOpen className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Step-by-step guides', icon: <ClipboardList className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Feature explanations', icon: <Lightbulb className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Troubleshooting', icon: <Wrench className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> },
        { text: 'Direct support contact', icon: <Phone className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> }
      ],
      benefit: 'Stuck? We\'re HERE for you. Real humans who care!'
    }
  ];

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featureSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, featureSlides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featureSlides.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featureSlides.length) % featureSlides.length);
    setIsAutoPlaying(false);
  };

  const languages = [
    'English', 'Español', 'Français', 'Deutsch', 'Italiano',
    'Português', '中文', '日本語', '한국어', 'العربية',
    'हिन्दी', 'Русский', 'Nederlands', 'Polski', 'Svenska'
  ];

  // Quick feature highlights for the stat bar
  const quickStats = [
    { number: '130+', label: 'Countries' },
    { number: '130+', label: 'Languages' },
    { number: 'Daily', label: 'New Quizzes' },
    { number: '2026', label: 'Biopic Year' }
  ];

  // Value proposition list
  const valueProps = [
    'Full PopCrown Community access (CONNECT)',
    'All 12 support rooms',
    'Daily horoscope & card readings',
    'Daily Poll & Daily Rewards',
    '30+ games & quizzes',
    'Unlimited quizzes',
    'Complete timeline & full music catalogue',
    'All videos',
    '340+ books library',
    'Legal Vault & Cadman Archives',
    'Annie AI — unlimited for members',
    '90-day WWMD programme',
    'Pop Icon Tourism guide',
    'Posts & discussions',
    'Points & 20-tier leaderboard',
    'PopCrown Community member badge',
    'VIP Chats',
    'And SO much more!'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* SEO Meta Tags and FAQ Schema */}
      <Helmet>
        <title>Install PopCrown FREE | Michael Jackson Fan App + PopCrown Community</title>
        <meta name="description" content="Install the PopCrown app FREE on iPhone or Android straight from your browser — daily MJ quizzes, the Legal Vault, In His Words, global leaderboard. Optional PopCrown Community (£1.99/month) unlocks the community feed, early event tickets and exclusive discounts. App Store coming soon." />
        <meta name="keywords" content="Michael Jackson free app, MJ fan app, install PopCrown, PopCrown Community, Michael Jackson community, King of Pop fans, Legal Vault, MJ quizzes, Jackson 5 fans, Thriller fans" />
        <link rel="canonical" href="https://popcrownofficial.com/app" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Install PopCrown FREE | Michael Jackson Fan App" />
        <meta property="og:description" content="The PopCrown app is free forever — install it on iPhone or Android from your browser. Optional PopCrown Community £1.99/month." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://popcrownofficial.com/app" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?q=80&w=1200" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Install PopCrown FREE | MJ Fan App" />
        <meta name="twitter:description" content="The only Michael Jackson fan app. Free forever. PopCrown Community £1.99/month." />
        
        {/* FAQ Schema for Google Rich Snippets */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #141414 50%, #0a0a0a 100%)'
      }}>
        {/* Subtle red ambient glow from the left */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 20% 50%, rgba(225, 6, 0, 0.22) 0%, transparent 55%)'
        }} />
        {/* Faint grain texture for depth */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'2\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")'
        }} />

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-8 md:gap-12 items-center min-h-[82vh] py-12 md:py-16">
            {/* LEFT — silver bust (cutout, no background) */}
            <div className="relative h-[65vh] md:h-[82vh] md:-ml-4 lg:-ml-16 order-1">
              {/* Red glow behind */}
              <div className="absolute inset-0 pointer-events-none" style={{
                background: 'radial-gradient(circle at 45% 45%, rgba(225, 6, 0, 0.45) 0%, transparent 60%)',
                filter: 'blur(20px)'
              }} />
              <picture>
                <source srcSet="/hero-mj-silver-cutout.webp" type="image/webp" />
                <img
                  src="/hero-mj-silver-cutout.png"
                  alt="Michael Jackson silver bust portrait — PopCrown free app for Michael Jackson fans"
                  className="absolute inset-0 w-full h-full object-contain object-left select-none"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </picture>
            </div>

            {/* RIGHT — copy */}
            <div className="text-left max-w-xl order-2">
              <div className="inline-block px-5 py-2 rounded-full mb-6" style={{
                background: 'linear-gradient(135deg, #DC143C 0%, #8B0000 100%)',
                boxShadow: '0 0 30px rgba(220, 20, 60, 0.5)'
              }}>
                <span className="inline-flex items-center gap-2 text-white font-bold text-xs md:text-sm tracking-wider"><Smartphone className="w-4 h-4" strokeWidth={2} aria-hidden="true" /> INSTALL FREE NOW · APP STORE COMING SOON</span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-[1.05]">
                <span className="thriller-text">PopCrown is FREE.</span>
              </h1>
              <p className="text-xl md:text-2xl text-white font-bold mb-3">
                The Michael Jackson Fan App. <span className="text-[#E10600]">No card needed.</span>
              </p>
              <p className="text-gray-300 text-base md:text-lg mb-8 leading-relaxed">
                Daily MJ quizzes, the Legal Vault, Michael in his own words, the global leaderboard — <strong className="text-white">all yours, free forever.</strong> Install it straight from your phone&apos;s browser today (full App Store &amp; Google Play listings coming soon). Want to <span className="text-[#E10600] font-bold">join the conversation</span>, grab <span className="text-[#E10600] font-bold">early event tickets</span> &amp; <span className="text-[#E10600] font-bold">discounts</span>? PopCrown Community is £1.99/month — it keeps the community safe &amp; moderated.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <a href="#install"
                   className="px-6 py-3 rounded-full font-bold text-base inline-flex items-center justify-center gap-2 transition-all hover:scale-105"
                   style={{
                     background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
                     color: '#000',
                     border: '2px solid #E10600',
                     boxShadow: '0 6px 25px rgba(225, 6, 0, 0.35)'
                   }}
                   data-testid="hero-download-free-btn">
                  <span className="inline-flex items-center justify-center gap-2"><Smartphone className="w-5 h-5" strokeWidth={2} aria-hidden="true" /> Install Free on Your Phone</span>
                </a>
                <a href={subscriptionJoinLink} target="_blank" rel="noopener noreferrer"
                   className="px-6 py-3 rounded-full font-bold text-base inline-flex items-center justify-center gap-2 transition-all hover:scale-105"
                   style={{
                     background: 'linear-gradient(135deg, #E10600 0%, #8B0000 100%)',
                     color: '#FFFFFF',
                     boxShadow: '0 6px 25px rgba(225, 6, 0, 0.5)'
                   }}
                   data-testid="hero-join-crown-btn">
                  Join PopCrown Community — £1.99/mo
                </a>
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-6 md:gap-8 mb-4">
                {quickStats.map((stat, index) => (
                  <div key={index} className="text-left">
                    <div className="text-2xl md:text-3xl font-bold text-[#E10600]">{stat.number}</div>
                    <div className="text-xs text-gray-400 tracking-wider uppercase">{stat.label}</div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-gray-500">
                (Not affiliated with any estates or rights holders)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Urgency Banner */}
      <section className="py-4" style={{ background: 'linear-gradient(90deg, #DC143C 0%, #E10600 50%, #DC143C 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <p className="text-white font-bold text-sm md:text-base inline-flex items-center gap-2">
            <Smartphone className="w-4 h-4" strokeWidth={2} aria-hidden="true" /> The app is FREE. PopCrown Community £1.99/month — join the conversation, get early event tickets &amp; discounts.
          </p>
        </div>
      </section>

      {/* How to Install (PWA) */}
      <section id="install" className="py-16 md:py-20 scroll-mt-20" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#DC143C] text-white rounded-full text-sm font-bold mb-4">
              INSTALL FREE · APP STORE COMING SOON
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              How to Install on Your <span className="thriller-text">iPhone or Android</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg">
              The full app, free, in under 30 seconds. It sits on your home screen, opens full-screen and works <strong>exactly like an App Store app</strong> — without waiting for the App Store. (Native App Store &amp; Google Play listings are on the way.)
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">

            {/* iPhone / iOS */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-100" data-testid="install-ios-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-black text-white flex items-center justify-center text-2xl"></div>
                <div>
                  <h3 className="text-xl font-bold text-black">iPhone &amp; iPad</h3>
                  <p className="text-sm text-gray-500">Safari (works the same on iPad)</p>
                </div>
              </div>
              <ol className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E10600] text-white font-bold flex items-center justify-center text-sm">1</span>
                  <p className="text-gray-800 pt-1">Open <strong>Safari</strong> and visit <strong>members.popcrownofficial.com</strong></p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E10600] text-white font-bold flex items-center justify-center text-sm">2</span>
                  <p className="text-gray-800 pt-1">Tap the <strong>Share</strong> button <span className="inline-block px-2 py-0.5 bg-gray-100 rounded text-sm">⬆️</span> at the bottom of the screen</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E10600] text-white font-bold flex items-center justify-center text-sm">3</span>
                  <p className="text-gray-800 pt-1">Scroll down and tap <strong>"Add to Home Screen"</strong></p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E10600] text-white font-bold flex items-center justify-center text-sm">4</span>
                  <p className="text-gray-800 pt-1">Tap <strong>"Add"</strong> in the top-right corner</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#DC143C] text-white font-bold flex items-center justify-center"><Check className="w-4 h-4" strokeWidth={3} aria-hidden="true" /></span>
                  <p className="text-gray-800 pt-1"><strong>Done.</strong> The PopCrown crown icon is now on your home screen. Tap it any time — it opens full-screen, just like an App Store app.</p>
                </li>
              </ol>
            </div>

            {/* Android */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-100" data-testid="install-android-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#3DDC84] text-white flex items-center justify-center"><Bot className="w-6 h-6" strokeWidth={2} aria-hidden="true" /></div>
                <div>
                  <h3 className="text-xl font-bold text-black">Android Phone &amp; Tablet</h3>
                  <p className="text-sm text-gray-500">Chrome (same steps on tablets)</p>
                </div>
              </div>
              <ol className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E10600] text-white font-bold flex items-center justify-center text-sm">1</span>
                  <p className="text-gray-800 pt-1">Open <strong>Chrome</strong> and visit <strong>members.popcrownofficial.com</strong></p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E10600] text-white font-bold flex items-center justify-center text-sm">2</span>
                  <p className="text-gray-800 pt-1">Tap the <strong>3-dot menu</strong> <span className="inline-block px-2 py-0.5 bg-gray-100 rounded text-sm">⋮</span> in the top-right</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E10600] text-white font-bold flex items-center justify-center text-sm">3</span>
                  <p className="text-gray-800 pt-1">Tap <strong>"Install app"</strong> (or <strong>"Add to Home screen"</strong> on some devices)</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E10600] text-white font-bold flex items-center justify-center text-sm">4</span>
                  <p className="text-gray-800 pt-1">Tap <strong>"Install"</strong> to confirm</p>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#DC143C] text-white font-bold flex items-center justify-center"><Check className="w-4 h-4" strokeWidth={3} aria-hidden="true" /></span>
                  <p className="text-gray-800 pt-1"><strong>Done.</strong> PopCrown is now in your app drawer and on your home screen — opens full-screen, exactly like any other app.</p>
                </li>
              </ol>
            </div>
          </div>

          {/* Reassurance row */}
          <div className="max-w-3xl mx-auto mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="p-3">
              <Smartphone className="w-6 h-6 mx-auto mb-1 text-[#E10600]" strokeWidth={1.5} aria-hidden="true" />
              <p className="text-xs text-gray-600 font-semibold">Works just like an app</p>
            </div>
            <div className="p-3">
              <Zap className="w-6 h-6 mx-auto mb-1 text-[#E10600]" strokeWidth={1.5} aria-hidden="true" />
              <p className="text-xs text-gray-600 font-semibold">30-second install</p>
            </div>
            <div className="p-3">
              <BadgeCheck className="w-6 h-6 mx-auto mb-1 text-[#E10600]" strokeWidth={1.5} aria-hidden="true" />
              <p className="text-xs text-gray-600 font-semibold">No card. No App Store fee.</p>
            </div>
            <div className="p-3">
              <RefreshCw className="w-6 h-6 mx-auto mb-1 text-[#E10600]" strokeWidth={1.5} aria-hidden="true" />
              <p className="text-xs text-gray-600 font-semibold">Always the latest version</p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Carousel Section */}
      <section className="py-20 overflow-hidden" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #0f0f0f 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              <span className="thriller-text">Everything</span> You Get
            </h2>
            <p className="text-gray-400 text-lg">Swipe through {featureSlides.length} incredible feature categories</p>
          </div>

          {/* Carousel Container */}
          <div className="relative max-w-5xl mx-auto" ref={carouselRef}>
            {/* Navigation Arrows */}
            <button 
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-12 z-20 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-all"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button 
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-12 z-20 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-all"
              aria-label="Next slide"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>

            {/* Slide */}
            <div className="overflow-hidden rounded-2xl">
              <div 
                className="transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                <div className="flex">
                  {featureSlides.map((slide, index) => (
                    <div 
                      key={slide.id}
                      className="w-full flex-shrink-0 px-2"
                    >
                      <div 
                        className="rounded-2xl p-6 md:p-10 relative overflow-hidden min-h-[520px] flex flex-col"
                        style={{
                          background: `linear-gradient(135deg, ${slide.color}20 0%, rgba(20, 20, 20, 0.95) 50%, ${slide.color}10 100%)`,
                          border: `2px solid ${slide.color}40`
                        }}
                      >
                        {/* Badge */}
                        {slide.badge && (
                          <span 
                            className="absolute top-4 right-4 px-3 py-1 rounded-full text-white text-xs font-bold animate-pulse"
                            style={{ background: slide.badgeColor }}
                          >
                            {slide.badge}
                          </span>
                        )}

                        {/* Header */}
                        <div className="flex items-center gap-4 mb-4">
                          <div 
                            className="p-3 rounded-xl"
                            style={{ background: `${slide.color}30`, color: slide.color }}
                          >
                            {slide.icon}
                          </div>
                          <div>
                            <p className="text-xs font-bold tracking-widest" style={{ color: slide.color }}>
                              {slide.subtitle}
                            </p>
                            <h3 className="text-xl md:text-2xl font-bold text-white">
                              {slide.title}
                            </h3>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-base text-gray-300 mb-4">
                          {slide.description}
                        </p>

                        {/* Highlights */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4 flex-grow">
                          {slide.highlights.map((highlight, hIndex) => (
                            <div 
                              key={hIndex}
                              className="flex items-center gap-2 p-2 rounded-lg"
                              style={{ background: 'rgba(255,255,255,0.05)' }}
                            >
                              <span className="text-[#E10600] flex items-center justify-center w-5 h-5">{highlight.icon}</span>
                              <span className="text-gray-200 text-sm">{highlight.text}</span>
                            </div>
                          ))}
                        </div>

                        {/* Benefit Callout */}
                        <div 
                          className="p-3 rounded-xl mt-auto"
                          style={{ background: `${slide.color}15`, borderLeft: `4px solid ${slide.color}` }}
                        >
                          <p className="text-white text-sm font-medium flex items-start gap-2">
                            <Zap className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: slide.color }} />
                            {slide.benefit}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Dot Indicators - Compact */}
            <div className="flex justify-center gap-1.5 mt-6 flex-wrap max-w-xl mx-auto">
              {featureSlides.map((slide, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentSlide === index 
                      ? 'w-6' 
                      : 'hover:opacity-75'
                  }`}
                  style={{ 
                    background: currentSlide === index ? slide.color : 'rgba(255,255,255,0.3)'
                  }}
                  aria-label={`Go to ${slide.title}`}
                />
              ))}
            </div>

            {/* Feature Counter */}
            <div className="text-center mt-4">
              <span className="text-gray-500 text-sm">
                {currentSlide + 1} / {featureSlides.length} Feature Categories
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* All Features Quick Grid */}
      <section className="py-16" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Everything at a <span className="thriller-text">Glance</span>
            </h2>
            <p className="text-gray-600">Click any feature to see details</p>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-3 max-w-6xl mx-auto">
            {featureSlides.map((feature, index) => (
              <button
                key={feature.id}
                onClick={() => {
                  goToSlide(index);
                  carouselRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                className="p-3 rounded-xl text-center hover:scale-105 transition-all group relative"
                style={{
                  background: currentSlide === index ? `${feature.color}15` : '#FFFFFF',
                  border: `2px solid ${currentSlide === index ? feature.color : '#E5E7EB'}`
                }}
              >
                {feature.badge && (
                  <span 
                    className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full text-white text-[8px] font-bold"
                    style={{ background: feature.badgeColor }}
                  >
                    {feature.badge}
                  </span>
                )}
                <div 
                  className="w-8 h-8 mx-auto mb-1 rounded-lg flex items-center justify-center transition-colors"
                  style={{ 
                    background: `${feature.color}20`,
                    color: feature.color
                  }}
                >
                  {React.cloneElement(feature.icon, { className: 'w-4 h-4' })}
                </div>
                <p className="text-[10px] font-medium text-gray-700 line-clamp-2 leading-tight">{feature.subtitle}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12" style={{ background: '#1a1a1a' }}>
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400 text-sm mb-4">Be part of something new - a safe community built by fans, for fans</p>
          <div className="flex justify-center items-center gap-2 flex-wrap">
            {['Real conversations', 'Global leaderboard', 'Daily challenges', 'Your people'].map((item, i) => (
              <span key={i} className="px-3 py-1 bg-[#E10600]/10 border border-[#E10600]/30 rounded-full text-[#E10600] text-xs">
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #2a2a2a 100%)' }}>
        <div className="container mx-auto px-4">
          {/* Banner */}
          <div className="text-center mb-12">
            <div className="inline-block px-8 py-3 rounded-full mb-6" style={{
              background: 'linear-gradient(135deg, #DC143C 0%, #8B0000 100%)',
              boxShadow: '0 0 30px rgba(220, 20, 60, 0.5)'
            }}>
              <span className="inline-flex items-center gap-2 text-white font-bold text-lg"><Smartphone className="w-5 h-5" strokeWidth={2} aria-hidden="true" /> INSTALL FREE NOW · APP STORE COMING SOON</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The App is <span className="thriller-text">FREE</span>
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Want to <strong className="text-white">join the conversation</strong>? That&apos;s the <strong className="text-[#E10600]">PopCrown Community</strong> — and it&apos;s less than a Costa.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch">
            {/* FREE APP — clean, no faded image */}
            <div className="p-8 pt-12 rounded-xl flex flex-col relative" style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
              border: '2px solid rgba(255, 255, 255, 0.2)'
            }} data-testid="app-free-card">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="inline-flex items-center gap-1.5 px-4 py-1 bg-black text-white text-sm font-bold rounded-full border border-white/30">
                  <Smartphone className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" /> NO CARD NEEDED
                </span>
              </div>
              <div className="flex flex-col flex-grow">
                <h3 className="text-2xl font-bold text-white mb-2">The App</h3>
                <div className="mb-4">
                  <span className="thriller-text text-6xl md:text-7xl font-extrabold" style={{ lineHeight: 1 }}>FREE</span>
                  <p className="text-sm text-gray-400 mt-2">Forever · iPhone &amp; Android</p>
                </div>
                <ul className="space-y-2 mb-8 flex-grow text-sm">
                  <li className="flex items-center gap-2 text-gray-200">
                    <Check className="w-4 h-4 text-[#E10600]" />
                    <span><strong>Daily MJ quizzes &amp; games</strong></span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-200">
                    <Check className="w-4 h-4 text-[#E10600]" />
                    <span><strong>The Legal Vault</strong> — Documents &amp; timelines</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-200">
                    <Check className="w-4 h-4 text-[#E10600]" />
                    <span><strong>In His Words</strong> — Michael&apos;s own quotes</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-200">
                    <Check className="w-4 h-4 text-[#E10600]" />
                    <span><strong>Global leaderboard</strong></span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-200">
                    <Check className="w-4 h-4 text-[#E10600]" />
                    <span>130+ languages supported</span>
                  </li>
                  <li className="flex items-center gap-2 text-gray-200">
                    <Check className="w-4 h-4 text-[#E10600]" />
                    <span>Zero ads. Zero spam. No paywall on the good stuff.</span>
                  </li>
                </ul>
                <a
                  href="#install"
                  className="w-full py-4 rounded-full font-bold text-lg transition-all hover:scale-105 block text-center mt-auto"
                  style={{
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
                    color: '#000000',
                    border: '2px solid #E10600',
                    boxShadow: '0 4px 20px rgba(225, 6, 0, 0.3)'
                  }}
                  data-testid="app-download-free-btn"
                >
                  Install Free on Your Phone →
                </a>
              </div>
            </div>

            {/* CROWN COMMUNITY — paid */}
            <div className="p-8 pt-12 rounded-xl relative flex flex-col" style={{
              background: 'linear-gradient(135deg, rgba(225, 6, 0, 0.15) 0%, rgba(220, 20, 60, 0.1) 100%)',
              border: '3px solid #E10600',
              boxShadow: '0 20px 60px rgba(225, 6, 0, 0.3)'
            }} data-testid="app-crown-card">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="px-6 py-2 font-bold text-sm rounded-full" style={{
                  background: 'linear-gradient(135deg, #E10600 0%, #8B0000 100%)',
                  color: '#FFFFFF',
                  boxShadow: '0 4px 15px rgba(225, 6, 0, 0.6)',
                  border: '2px solid #DC143C'
                }}>
                  <span className="inline-flex items-center gap-2"><Crown className="w-4 h-4" strokeWidth={2} aria-hidden="true" /> THE INNER CIRCLE <Crown className="w-4 h-4" strokeWidth={2} aria-hidden="true" /></span>
                </span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">PopCrown Community</h3>
              <div className="mb-4">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-6xl md:text-7xl font-bold text-[#E10600]" style={{ lineHeight: 1 }}>£1.99</span>
                  <span className="text-gray-400 text-lg ml-1">/month</span>
                </div>
                <p className="text-sm text-gray-400 mt-1">Less than 7p a day · Cancel anytime</p>
              </div>
              <ul className="space-y-2 mb-8 flex-grow text-sm">
                <li className="flex items-center gap-2 text-gray-200">
                  <Check className="w-4 h-4 text-[#E10600]" />
                  <span><strong>Everything in the free app</strong></span>
                </li>
                <li className="flex items-center gap-2 text-white bg-[#E10600]/20 p-2 rounded -mx-2">
                  <Check className="w-4 h-4 text-[#E10600]" />
                  <span><strong>Join the conversation</strong> — community feed with fans in 130+ countries</span>
                </li>
                <li className="flex items-center gap-2 text-white bg-[#E10600]/20 p-2 rounded -mx-2">
                  <Check className="w-4 h-4 text-[#E10600]" />
                  <span><strong>Early access to event tickets</strong></span>
                </li>
                <li className="flex items-center gap-2 text-white bg-[#E10600]/20 p-2 rounded -mx-2">
                  <Check className="w-4 h-4 text-[#E10600]" />
                  <span><strong>Real discount codes</strong> — merch, tickets, perks</span>
                </li>
                <li className="flex items-center gap-2 text-gray-200">
                  <Check className="w-4 h-4 text-[#E10600]" />
                  <span>Fan Points &amp; exclusive members-only badges</span>
                </li>
                <li className="flex items-start gap-2 text-gray-300 text-xs italic mt-3 pt-3 border-t border-white/10">
                  <Shield className="w-4 h-4 text-[#E10600] shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
                  <span>Your fee keeps the community safe, moderated &amp; legally compliant.</span>
                </li>
              </ul>
              <div className="mt-auto" data-testid="app-crown-join-btn">
                <stripe-buy-button
                  buy-button-id="buy_btn_1TlVm3EGgMgYLhYhjcfnIfe0"
                  publishable-key="pk_live_51Sgn32EGgMgYLhYhWQ8WMBIiVvNSt7JcpG4U9rulRMmwgfE18K1sREeSLn0zrXcgFi45SOnmD7FlIxT4n9LTkASR00UzJhyl7m"
                >
                </stripe-buy-button>
                <p className="text-center text-xs text-gray-400 mt-2">£1.99 per month · Cancel anytime</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Languages Section */}
      <section className="py-16" style={{ background: 'linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <Globe className="w-12 h-12 text-[#E10600] mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-black mb-4">
            Available in <span className="thriller-text">130+ Languages</span>
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            His music united the WORLD. So do we.
          </p>
          <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
            {languages.map((lang, index) => (
              <span 
                key={index}
                className="px-4 py-2 rounded-full text-sm font-medium"
                style={{
                  background: 'rgba(225, 6, 0, 0.1)',
                  border: '1px solid rgba(225, 6, 0, 0.3)',
                  color: '#333'
                }}
              >
                {lang}
              </span>
            ))}
            <span 
              className="px-4 py-2 rounded-full text-sm font-medium"
              style={{
                background: 'rgba(220, 20, 60, 0.1)',
                border: '1px solid rgba(220, 20, 60, 0.3)',
                color: '#DC143C'
              }}
            >
              + 115 more
            </span>
          </div>
        </div>
      </section>

      {/* MJVibe Review Section */}
      <section className="py-16" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #0f0f0f 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E10600]/10 text-[#E10600] rounded-full text-sm font-semibold mb-4">
              <Star className="w-4 h-4" />
              INDEPENDENT REVIEW
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
              What <span className="thriller-text">MJVibe</span> Says
            </h2>
            <p className="text-gray-400">Honest review from a trusted MJ fan community</p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Main Quote */}
            <div className="p-8 rounded-2xl mb-8 text-center" style={{
              background: 'linear-gradient(135deg, rgba(225, 6, 0, 0.1) 0%, rgba(220, 20, 60, 0.1) 100%)',
              border: '2px solid rgba(225, 6, 0, 0.3)'
            }}>
              <blockquote className="text-xl md:text-2xl text-white mb-6 leading-relaxed" style={{ fontStyle: 'italic' }}>
                "I wholeheartedly recommend supporting this initiative. It is thoughtful, well-constructed, and rooted in care for both the artist and the community."
              </blockquote>
              <div className="flex items-center justify-center gap-3">
                <img 
                  src="https://www.mjvibe.com/wp-content/uploads/2021/01/MJVIBE-01-Logoa.png" 
                  alt="MJVibe Logo" 
                  className="h-8"
                />
                <span className="text-gray-400">— Sebastian, MJVibe</span>
              </div>
            </div>

            {/* Additional Quotes Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="p-6 rounded-xl" style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <p className="text-gray-300 text-sm leading-relaxed">
                  "PopCrown Social is not just another fan app, it is an ambitious attempt to build a dedicated digital home for those who want something more meaningful."
                </p>
              </div>
              <div className="p-6 rounded-xl" style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <p className="text-gray-300 text-sm leading-relaxed">
                  "The atmosphere is noticeably calmer than what many fans have grown used to elsewhere."
                </p>
              </div>
              <div className="p-6 rounded-xl" style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <p className="text-gray-300 text-sm leading-relaxed">
                  "PopCrown Social is more than a content app. It is a statement about ownership, dignity, and preservation."
                </p>
              </div>
              <div className="p-6 rounded-xl" style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <p className="text-gray-300 text-sm leading-relaxed">
                  "A New Digital Sanctuary for Michael Jackson Fans... a proactive effort by fans to create their own space."
                </p>
              </div>
            </div>

            {/* Read Full Review Link */}
            <div className="text-center">
              <a 
                href="https://www.mjvibe.com/popcrown-social-review-a-new-digital-sanctuary-for-michael-jackson-fans/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all hover:scale-105"
                style={{
                  background: 'transparent',
                  border: '2px solid #E10600',
                  color: '#E10600'
                }}
              >
                Read Full Review on MJVibe
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #FFF5F5 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block px-6 py-2 rounded-full mb-6" style={{
            background: 'linear-gradient(135deg, #DC143C 0%, #8B0000 100%)',
          }}>
            <span className="inline-flex items-center gap-2 text-white font-bold"><Clapperboard className="w-4 h-4" strokeWidth={2} aria-hidden="true" /> THE COUNTDOWN IS ON</span>
          </div>
          <img src="/popcrown-crown-red.png" alt="" aria-hidden="true" className="w-12 h-12 mx-auto mb-6 object-contain" />
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
            The <span className="thriller-text">Celebration</span> Is Happening
          </h2>
          <p className="text-gray-600 mb-4 max-w-2xl mx-auto">
            130+ countries. 130+ languages. The Michael biopic was the biggest movie event of 2026 — fans worldwide watched together. The conversation hasn&apos;t stopped. Don&apos;t watch from the sidelines.
          </p>
          <p className="text-[#DC143C] font-bold mb-8">
            Join the global MJ community. Be part of history.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
            <a
              href="#install"
              className="px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 inline-block"
              style={{
                background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
                color: '#000000',
                border: '2px solid #E10600',
                boxShadow: '0 4px 20px rgba(225, 6, 0, 0.3)'
              }}
              data-testid="app-footer-download-btn"
            >
              <span className="inline-flex items-center justify-center gap-2"><Smartphone className="w-5 h-5" strokeWidth={2} aria-hidden="true" /> Install Free on Your Phone</span>
            </a>
            <a 
              href={subscriptionJoinLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 inline-block"
              style={{
                background: 'linear-gradient(135deg, #E10600 0%, #8B0000 100%)',
                color: '#FFFFFF',
                boxShadow: '0 4px 20px rgba(225, 6, 0, 0.4)'
              }}
              data-testid="app-footer-crown-btn"
            >
              Join PopCrown Community — £1.99/mo
            </a>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Free app forever • PopCrown Community cancel anytime • 130+ languages
          </p>
        </div>
      </section>
    </div>
  );
};

export default AppPage;
